import { motion } from 'framer-motion';
import { AlertCircle, User, Camera, MapPin, FileText, ChevronRight } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card, CardContent } from '../ui/Card';
import { Progress } from '../ui/Progress';
import { useAuth } from '../../hooks/useAuth';

const ProfileCompletionBanner = ({ onCompleteProfile }) => {
  const { user, getProfileCompletion, getMissingProfileFields, isProfileComplete } = useAuth();
  
  // Don't show if profile is complete
  if (isProfileComplete()) return null;
  
  const completion = getProfileCompletion();
  const missingFields = getMissingProfileFields();
  
  const fieldIcons = {
    'Full Name': User,
    'Profile Photo': Camera,
    'Country': MapPin,
    'Bio': FileText
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-6"
    >
      <Card className="border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-amber-600" />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-amber-900 mb-2">
                    Complete Your Profile to Unlock Project Creation
                  </h3>
                  <p className="text-amber-800 text-sm mb-3">
                    You're {completion}% there! Complete the missing fields to start creating projects.
                  </p>
                  
                  {/* Progress Bar */}
                  <div className="mb-3">
                    <Progress value={completion} className="h-2 bg-amber-200" />
                  </div>
                  
                  {/* Missing Fields */}
                  <div className="flex flex-wrap gap-2">
                    {missingFields.map((field) => {
                      const Icon = fieldIcons[field] || FileText;
                      return (
                        <div key={field} className="flex items-center gap-1 text-xs bg-white/60 px-2 py-1 rounded-full text-amber-700">
                          <Icon className="w-3 h-3" />
                          {field}
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                <Button
                  onClick={onCompleteProfile}
                  className="bg-amber-600 hover:bg-amber-700 text-white flex-shrink-0"
                >
                  Complete Profile
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default ProfileCompletionBanner;